import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
      {/* Header */}
      <header className="relative flex justify-between items-center p-6 border-b border-gray-800">
        <div className="flex items-center gap-3">
          <img src="/quantumx2.png" alt="QuantumXAI" className="h-12 cursor-pointer" onClick={() => navigate('/')} />
        </div>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white transition"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </button>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="h-8 w-8 text-blue-500" />
          <h1 className="text-4xl font-bold">Privacy Policy</h1>
        </div>
        <p className="text-gray-400 mb-8">Last Updated: November 11, 2025</p>

        <div className="prose prose-invert max-w-none space-y-8">
          {/* Introduction */}
          <section className="bg-blue-500/10 border border-blue-500/30 p-6 rounded-lg">
            <h2 className="text-2xl font-semibold mb-4">Introduction</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Welcome to QuantumXAI ("QXAI," "we," "us," or "our"). This Privacy Policy explains how we collect, use, 
              disclose, and safeguard your information when you use our privacy-first AI platform at quantumxai.web.app (the "Service").
            </p>
            <div className="bg-green-500/10 border border-green-500/30 p-4 rounded-lg">
              <h3 className="text-xl font-semibold mb-2 text-green-400">🔒 Our Core Principle: AI Without Surveillance</h3>
              <p className="text-gray-300 leading-relaxed">
                We minimize the personal information associated with your activity. Your prompts, conversations and
                outputs are used to provide the service. We never sell your information to third parties.
              </p>
            </div>
          </section>

          {/* 1. Information We Collect */}
          <section>
            <h2 className="text-2xl font-semibold mb-4">1. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold mb-3 text-blue-400">1.1 Hash-Based Authentication</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
              <li><strong>Sign-in:</strong> Your email address and password are used by Firebase Authentication (Google Identity) to sign you in. Alongside this, your browser computes a SHA-256 hash of your normalized email address, and that hash is what identifies your account on our application servers.</li>
              <li><strong>Account record:</strong> Your account record stores the hash, not your email address. Email addresses remain in invitation records, operational logs and a separate account-mapping record; removing them is planned.</li>
              <li><strong>Password:</strong> Managed by Firebase Authentication. We do not have access to your password.</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-blue-400">1.2 Account Information</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
              <li><strong>User Hash:</strong> A unique identifier derived from your email hash</li>
              <li><strong>Account Tier:</strong> Your subscription level (Starter, Pro, Super Pro, or Free)</li>
              <li><strong>Credit Balance:</strong> Number of AI credits remaining in your account</li>
              <li><strong>Usage Data:</strong> Aggregate usage statistics for billing purposes</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-blue-400">1.3 Payment Information</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
              <li><strong>Stripe Processing:</strong> All payment information is processed by Stripe, Inc. We do not store your credit card numbers, billing addresses, or other payment details on our servers.</li>
              <li><strong>Subscription Status:</strong> Information about your active subscriptions and payment history</li>
              <li><strong>Transaction Records:</strong> Records of credit purchases and subscription payments</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-blue-400">1.4 Technical Information</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
              <li><strong>Browser Type:</strong> Information about the web browser you use</li>
              <li><strong>Device Information:</strong> Type of device and operating system</li>
              <li><strong>IP Address:</strong> For security and fraud prevention purposes</li>
              <li><strong>Usage Logs:</strong> Timestamps and types of AI operations performed</li>
              <li><strong>Performance Metrics:</strong> Service uptime and response times</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-blue-400">1.5 AI Interaction Data</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
              <li><strong>Prompts and Responses:</strong> Your conversations with AI models are processed to provide the Service</li>
              <li><strong>Chat History:</strong> Stored locally in your browser and optionally in Firebase for multi-device access</li>
              <li><strong>Generated Content:</strong> Images, videos, and other AI-generated content</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-blue-400">1.6 Cookies and Similar Technologies</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li><strong>Essential Cookies:</strong> Required for authentication and service functionality</li>
              <li><strong>Analytics Cookies:</strong> To understand how users interact with our Service (can be opted out)</li>
              <li><strong>Session Tokens:</strong> Temporary tokens for maintaining your logged-in state</li>
            </ul>
          </section>

          {/* 2. How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-semibold mb-4">2. How We Use Your Information</h2>
            <p className="text-gray-300 mb-4">We use the information we collect for the following purposes:</p>

            <h3 className="text-xl font-semibold mb-3 text-blue-400">2.1 Service Delivery</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
              <li>Authenticate your identity using hash-based verification</li>
              <li>Process your AI requests and deliver results</li>
              <li>Manage your credit balance and subscriptions</li>
              <li>Enable multi-device access to your account</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-blue-400">2.2 Billing and Payments</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
              <li>Process subscription payments through Stripe</li>
              <li>Handle credit package purchases</li>
              <li>Send payment receipts and invoices</li>
              <li>Manage refunds and chargebacks</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-blue-400">2.3 Service Improvement</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
              <li>Monitor service performance and uptime</li>
              <li>Identify and fix technical issues</li>
              <li>Develop new features and capabilities</li>
              <li>Optimize AI model performance</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-blue-400">2.4 Security and Fraud Prevention</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
              <li>Detect and prevent fraudulent activity</li>
              <li>Protect against unauthorized access</li>
              <li>Monitor for abuse and violations of Terms of Service</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          {/* 3. How We Share Your Information */}
          <section>
            <h2 className="text-2xl font-semibold mb-4">3. How We Share Your Information</h2>
            <p className="text-gray-300 mb-4 font-semibold">We do not sell, rent, or trade your personal information.</p>
            <p className="text-gray-300 mb-4">We may share information in the following limited circumstances:</p>

            <h3 className="text-xl font-semibold mb-3 text-blue-400">3.1 Service Providers</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
              <li><strong>Google Cloud Platform:</strong> Infrastructure hosting (data encrypted at rest)</li>
              <li><strong>Firebase:</strong> Authentication and database services</li>
              <li><strong>Stripe:</strong> Payment processing (see Stripe's Privacy Policy)</li>
              <li><strong>OpenRouter/ImageRouter:</strong> AI model routing services</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-blue-400">3.2 Legal Requirements</h3>
            <p className="text-gray-300 mb-4">We may disclose information if required by law or to:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li>Comply with legal process or government requests</li>
              <li>Enforce our Terms of Service</li>
              <li>Protect our rights, property, or safety</li>
              <li>Prevent fraud or security threats</li>
            </ul>
          </section>

          {/* 4. Your Rights */}
          <section className="bg-purple-500/10 border border-purple-500/30 p-6 rounded-lg">
            <h2 className="text-2xl font-semibold mb-4">4. Your Rights</h2>
            
            <h3 className="text-xl font-semibold mb-3 text-purple-400">4.1 Access and Portability</h3>
            <p className="text-gray-300 mb-4">You have the right to access your data and export it in a portable format.</p>

            <h3 className="text-xl font-semibold mb-3 text-purple-400">4.2 Deletion</h3>
            <p className="text-gray-300 mb-4">You may delete your account at any time through Settings. This will permanently delete your data.</p>

            <h3 className="text-xl font-semibold mb-3 text-purple-400">4.3 Opt-Out</h3>
            <p className="text-gray-300 mb-4">You may opt out of non-essential cookies and analytics tracking.</p>

            <h3 className="text-xl font-semibold mb-3 text-purple-400">4.4 GDPR Rights (EU Users)</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
              <li>Right to access your personal data</li>
              <li>Right to rectification of inaccurate data</li>
              <li>Right to erasure ("right to be forgotten")</li>
              <li>Right to restrict processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-purple-400">4.5 CCPA Rights (California Users)</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li>Right to know what personal information is collected</li>
              <li>Right to delete personal information</li>
              <li>Right to opt-out of sale of personal information (we do not sell data)</li>
              <li>Right to non-discrimination for exercising CCPA rights</li>
            </ul>
          </section>

          {/* 5. Privacy and Data Retention */}
          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Privacy and Data Retention</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Stored conversations expire after 90 days of inactivity; individual turns and steps expire 90 days
              after they&rsquo;re written. Our database&rsquo;s automatic expiry process permanently deletes expired sessions,
              turns, runs and steps. The expired backlog in those collections has been purged, as verified on
              October 30, 2026. Your account record stores an account hash rather than an email address. Email
              addresses remain in invitation records, operational logs and a separate account-mapping record;
              removing them is planned.
            </p>
            <p className="text-gray-300 leading-relaxed">
              Saved agent checkpoints expire in the app after 90 days, but automatic database deletion is not
              enabled for them.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-gray-800/30 p-6 rounded-lg">
            <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              If you have questions about this Privacy Policy or wish to exercise your rights, please contact us at:
            </p>
            <ul className="text-gray-300 space-y-2">
              <li><strong>Privacy Email:</strong> privacy@quantumxai.com</li>
              <li><strong>Support:</strong> support@quantumxai.com</li>
              <li><strong>Data Protection Officer:</strong> dpo@quantumxai.com</li>
            </ul>
          </section>

          {/* Footer note */}
          <div className="text-center text-gray-500 text-sm pt-8 border-t border-gray-800">
            <p>🔒 Your privacy is our priority. AI Without Surveillance.</p>
          </div>
        </div>
      </main>
    </div>
  );
}
